import React, { useState, useEffect, useCallback } from 'react';
import { X, Settings, User, Lock, Eye, EyeOff, Check, AlertCircle, MapPin, Palette } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../services/api';
import BillingAddressForm from './BillingAddressForm';
import { validateSiret, validateVatNumber } from '../../utils/businessIdValidation';

const ANIMATION_DURATION = 150;

function AccountSettingsModal({ isOpen, onClose, initialTab = 'profile' }) {
  const { user, updateProfile, changePassword, updateTheme, isProsthetist, isAdmin } = useAuth();
  const showBankDetails = isProsthetist() || isAdmin();

  const [activeTab, setActiveTab] = useState('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  const [profileForm, setProfileForm] = useState({
    name: '',
    clinicName: '',
    phone: '',
    address: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [billingForm, setBillingForm] = useState({
    billingName: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingPostalCode: '',
    billingCity: '',
    billingCountry: 'France',
    siret: '',
    vatNumber: '',
    billingBeneficiary: '',
    billingIban: '',
    billingBic: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Gestion de la fermeture animée
  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, ANIMATION_DURATION);
  }, [isClosing, onClose]);

  // Initialiser le formulaire à l'ouverture uniquement : ne pas dépendre de
  // `user` sinon toute mise à jour du profil/thème (UPDATE_USER) réinitialise
  // la modale (retour à l'onglet initial, reset des formulaires).
  useEffect(() => {
    if (user && isOpen) {
      setIsClosing(false);
      setProfileForm({
        name: user.name || '',
        clinicName: user.clinic_name || '',
        phone: user.phone || '',
        address: user.address || ''
      });
      // Reset password form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError('');
      setSuccess('');
      setActiveTab(initialTab);

      // Load billing address
      const loadBilling = async () => {
        try {
          const billingAddress = await authApi.getBillingAddress();
          if (billingAddress) {
            setBillingForm(billingAddress);
          } else {
            setBillingForm({
              billingName: '',
              billingAddressLine1: '',
              billingAddressLine2: '',
              billingPostalCode: '',
              billingCity: '',
              billingCountry: 'France',
              siret: '',
              vatNumber: '',
              billingBeneficiary: '',
              billingIban: '',
              billingBic: ''
            });
          }
        } catch (err) {
          console.error('Error loading billing address:', err);
        }
      };
      loadBilling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTab]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Le mot de passe doit contenir au moins une minuscule';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Le mot de passe doit contenir au moins une majuscule';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Le mot de passe doit contenir au moins un chiffre';
    }
    if (!/\W/.test(password)) {
      return 'Le mot de passe doit contenir au moins un caractère spécial';
    }
    return null;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!profileForm.name.trim()) {
      setError('Le nom est requis');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await updateProfile({
        name: profileForm.name.trim(),
        clinicName: profileForm.clinicName?.trim() || null,
        phone: profileForm.phone?.trim() || null,
        address: profileForm.address?.trim() || null
      });

      if (result.success) {
        setSuccess('Profil mis à jour avec succès');
      } else {
        setError(result.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour du profil');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!passwordForm.currentPassword) {
      setError('Le mot de passe actuel est requis');
      return;
    }

    if (!passwordForm.newPassword) {
      setError('Le nouveau mot de passe est requis');
      return;
    }

    const passwordError = validatePassword(passwordForm.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      if (result.success) {
        setSuccess('Mot de passe modifié avec succès');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setError(result.error || 'Erreur lors du changement de mot de passe');
      }
    } catch (err) {
      setError('Erreur lors du changement de mot de passe');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleThemeSelect = async (theme) => {
    if (theme === (user?.theme || 'glass')) return;
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      const result = await updateTheme(theme);
      if (result.success) {
        setSuccess('Thème mis à jour');
      } else {
        setError(result.error || 'Erreur lors du changement de thème');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!billingForm.billingName?.trim()) {
      setError('Le nom ou raison sociale est requis');
      return;
    }
    if (!billingForm.billingAddressLine1?.trim()) {
      setError('L\'adresse est requise');
      return;
    }
    if (!billingForm.billingPostalCode?.trim()) {
      setError('Le code postal est requis');
      return;
    }
    if (!billingForm.billingCity?.trim()) {
      setError('La ville est requise');
      return;
    }
    const siretCheck = validateSiret(billingForm.siret);
    if (!siretCheck.valid) {
      setError(siretCheck.error);
      return;
    }
    const vatCheck = validateVatNumber(billingForm.vatNumber);
    if (!vatCheck.valid) {
      setError(vatCheck.error);
      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.updateBillingAddress(billingForm);
      setSuccess('Adresse de facturation mise à jour avec succès');
    } catch (err) {
      setError('Erreur lors de la mise à jour de l\'adresse de facturation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 overlay-frost z-50 overflow-y-auto ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="min-h-full flex items-center justify-center p-4">
        <div className={`glass-deep border border-white/10 rounded-lg shadow-xl max-w-lg w-full ${isClosing ? 'animate-modal-out' : 'animate-modal-in'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-500/20 p-2 rounded-lg">
              <Settings className="h-6 w-6 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Paramètres du compte
              </h2>
              <p className="text-sm text-gray-400">
                Gérez vos informations personnelles
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 bg-black/30 p-1 rounded-lg">
            <button
              onClick={() => { setActiveTab('profile'); setError(''); setSuccess(''); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'profile'
                  ? 'bg-yellow-500 text-black'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/10'
              }`}
            >
              <User className="h-4 w-4" />
              <span>Profil</span>
            </button>
            <button
              onClick={() => { setActiveTab('security'); setError(''); setSuccess(''); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'security'
                  ? 'bg-yellow-500 text-black'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/10'
              }`}
            >
              <Lock className="h-4 w-4" />
              <span>Sécurité</span>
            </button>
            <button
              onClick={() => { setActiveTab('billing'); setError(''); setSuccess(''); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'billing'
                  ? 'bg-yellow-500 text-black'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/10'
              }`}
            >
              <MapPin className="h-4 w-4" />
              <span>Facturation</span>
            </button>
            <button
              onClick={() => { setActiveTab('theme'); setError(''); setSuccess(''); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'theme'
                  ? 'bg-yellow-500 text-black'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/10'
              }`}
            >
              <Palette className="h-4 w-4" />
              <span>Thème</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Messages */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 mb-4 flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-md p-4 mb-4 flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-green-400 text-sm">{success}</p>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 text-gray-400 rounded-md cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">
                  L'adresse email ne peut pas être modifiée
                </p>
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={profileForm.name}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profileForm.phone}
                  onChange={handleProfileChange}
                  placeholder="06 12 34 56 78"
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              {/* Clinic Name */}
              <div>
                <label htmlFor="clinicName" className="block text-sm font-medium text-gray-300 mb-2">
                  Nom de l'organisation
                </label>
                <input
                  type="text"
                  id="clinicName"
                  name="clinicName"
                  value={profileForm.clinicName}
                  onChange={handleProfileChange}
                  placeholder="Cabinet dentaire, laboratoire..."
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              {/* Address */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-2">
                  Adresse
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={profileForm.address}
                  onChange={handleProfileChange}
                  rows="2"
                  placeholder="Adresse complète"
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                  disabled={isSubmitting}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-300 bg-white/15 border border-white/15 rounded-md hover:bg-white/20 transition-colors"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-white/10 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* Current Password */}
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Mot de passe actuel *
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-3 py-2 pr-10 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Nouveau mot de passe *
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-3 py-2 pr-10 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Au moins 8 caractères avec une minuscule, une majuscule, un chiffre et un caractère spécial
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Confirmer le nouveau mot de passe *
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-3 py-2 pr-10 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-300 bg-white/15 border border-white/15 rounded-md hover:bg-white/20 transition-colors"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-white/10 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Modification...' : 'Changer le mot de passe'}
                </button>
              </div>
            </form>
          )}

          {/* Theme Tab */}
          {activeTab === 'theme' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Choisissez l'apparence de l'interface. Le changement est appliqué immédiatement.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleThemeSelect('glass')}
                  disabled={isSubmitting}
                  className={`text-left p-4 rounded-lg border transition-all ${
                    (user?.theme || 'glass') === 'glass'
                      ? 'border-yellow-500/60 bg-yellow-500/10'
                      : 'border-white/10 hover:border-white/30 hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="h-10 rounded-md mb-3 border border-white/15 bg-gradient-to-br from-yellow-500/30 via-gray-900 to-black" />
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium text-sm">Verre</span>
                    {(user?.theme || 'glass') === 'glass' && <Check className="h-4 w-4 text-yellow-400" />}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Effet verre et halos dorés</p>
                </button>
                <button
                  type="button"
                  onClick={() => handleThemeSelect('legacy')}
                  disabled={isSubmitting}
                  className={`text-left p-4 rounded-lg border transition-all ${
                    user?.theme === 'legacy'
                      ? 'border-yellow-500/60 bg-yellow-500/10'
                      : 'border-white/10 hover:border-white/30 hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="h-10 rounded-md mb-3 border border-white/15 bg-black" />
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium text-sm">Classique</span>
                    {user?.theme === 'legacy' && <Check className="h-4 w-4 text-yellow-400" />}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Ancien thème sombre uni</p>
                </button>
              </div>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <form onSubmit={handleBillingSubmit} className="space-y-4">
              <p className="text-sm text-gray-400 mb-4">
                Cette adresse sera pré-remplie automatiquement lors de vos commandes.
              </p>

              <BillingAddressForm
                billingData={billingForm}
                onChange={setBillingForm}
                disabled={isSubmitting}
                compact={true}
                showBankDetails={showBankDetails}
              />

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-300 bg-white/15 border border-white/15 rounded-md hover:bg-white/20 transition-colors"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-white/10 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSettingsModal;
